import * as React from "react";
import Helmet from "react-helmet";
import { useStaticQuery, graphql } from "gatsby";
import GoogleTagManager from "./organisms/GoogleTagManager";

type SiteMetadata = {
  title: string;
  description: string;
  author: string;
  baseUrl: string;
  css: (string | Record<string, any>)[];
  script: (string | Record<string, any>)[];
};

type Site = {
  siteMetadata: SiteMetadata;
};

type QueryResult = {
  site: Site;
};

type HelmetProps = Partial<{
  description: string;
  lang: string;
  meta: {
    name: string;
    content: string;
  }[];
  link: React.LinkHTMLAttributes<HTMLLinkElement>[];
  keywords: string[];
  title: string;
  canonical: string;
  googleTagManagerId: string;
}>;

export default ({
  description,
  lang = "ja",
  meta = [],
  link = [],
  title,
  canonical = "/",
  googleTagManagerId,
}: HelmetProps) => {
  const { site }: QueryResult = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            title
            description
            author
            baseUrl
            script
            css
          }
        }
      }
    `
  );

  const metaDescription = description || site.siteMetadata.description;

  const selectedTitle = title ? `${title}` : site.siteMetadata.title;

  return (
    <>
      <Helmet
        htmlAttributes={{
          lang,
        }}
        title={selectedTitle}
        meta={[
          {
            name: `description`,
            content: metaDescription,
          },
          {
            property: `og:title`,
            content: selectedTitle,
          },
          {
            property: `og:description`,
            content: metaDescription,
          },
          {
            property: `og:type`,
            content: `website`,
          },
          {
            httpEquiv: "X-UA-Compatible",
            content: `IE=edge`,
          },
        ].concat(meta)}
        link={[
          {
            rel: "shortcut icon",
            href: `/assets/images/favicon.ico`,
          },
          {
            rel: "canonical",
            href: `${site.siteMetadata.baseUrl}${canonical}`,
          },
          ...site.siteMetadata.css.map(x => (typeof x === "string" ? { rel: "stylesheet", href: x } : x)),
        ].concat(link)}
        script={[...(site.siteMetadata.script || []).map(x => (typeof x === "string" ? { src: x } : x))]}
      />
      <GoogleTagManager gtmId={googleTagManagerId} />
    </>
  );
};
